import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Trash2, X } from "react-feather";

import ShowRows from "../../../components/table/ShowRows";
import { getUserDataByIdApi } from "../../../redux/sagas/requests/userRequests";
import { cn } from "../../../utils/commonFunctions";

export const ghostBtn =
  "inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E1383B]/30 disabled:cursor-not-allowed disabled:opacity-50";

export const dangerGhostBtn =
  "inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-red-200 bg-white px-3 text-sm font-medium text-red-600 transition hover:bg-red-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500/40";

export const primaryBtn =
  "inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#E1383B] to-[#EF7F1A] px-3.5 text-sm font-semibold text-white shadow-md shadow-[#E1383B]/25 transition hover:brightness-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E1383B]/40 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60";

export const iconBtn =
  "inline-flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 bg-white transition disabled:cursor-not-allowed disabled:opacity-40";

const pagerBtn =
  "flex h-8 min-w-8 items-center justify-center rounded-md border border-slate-200 bg-white px-2 text-xs font-medium text-slate-600 transition hover:border-[#E1383B]/40 hover:text-[#E1383B] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:text-slate-600";

const badgeBase =
  "inline-flex w-fit items-center rounded-md px-1.5 py-0.5 text-[11px] font-medium ring-1 ring-inset";

export const getPaymentBadgeClass = (status?: string | null) =>
  cn(badgeBase, {
    "bg-emerald-50 text-emerald-700 ring-emerald-200": status === "PAID",
    "bg-red-50 text-red-600 ring-red-200": status === "UNPAID",
    "bg-slate-100 text-slate-600 ring-slate-200": status !== "PAID" && status !== "UNPAID",
  });

export const getQcBadgeClass = (status?: string | null) =>
  cn(badgeBase, {
    "bg-emerald-50 text-emerald-700 ring-emerald-200": status === "GENERATED",
    "bg-amber-50 text-amber-700 ring-amber-200": status === "DRAFT",
    "bg-red-50 text-red-600 ring-red-200": status === "PENDING",
    "bg-slate-100 text-slate-600 ring-slate-200": !["GENERATED", "DRAFT", "PENDING"].includes(
      status || ""
    ),
  });

export const Badge = ({ label, className }: { label?: string | null; className: string }) =>
  label ? <span className={className}>{label.replace(/_/g, " ")}</span> : <Dash />;

export const Dash = () => <span className="text-slate-300">—</span>;

const inrFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export const formatAmount = (value?: string | number | null) => {
  if (value === null || value === undefined || value === "") return "—";
  const amount = Number(value);
  return Number.isFinite(amount) ? inrFormatter.format(amount) : String(value);
};

export const formatWeight = (value?: string | number | null) => {
  if (value === null || value === undefined || value === "") return "—";
  const weight = Number(value);
  return Number.isFinite(weight) ? `${weight.toLocaleString("en-IN")} kg` : String(value);
};

export type Crumb = { label: string; to?: string };

export const Breadcrumbs = ({ items }: { items: Crumb[] }) => (
  <nav aria-label="breadcrumb" className="flex flex-wrap items-center gap-1.5 text-xs text-slate-400">
    {items.map((item, index) => (
      <span key={item.label} className="flex items-center gap-1.5">
        {index > 0 && <ChevronRight size={12} />}
        {item.to ? (
          <Link to={item.to} className="transition hover:text-[#E1383B]">
            {item.label}
          </Link>
        ) : (
          <span className="font-medium text-slate-600">{item.label}</span>
        )}
      </span>
    ))}
  </nav>
);

export const CardHeader = ({
  icon,
  title,
  subtitle,
  right,
}: {
  icon: ReactNode;
  title: string;
  subtitle?: ReactNode;
  right?: ReactNode;
}) => (
  <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-5 py-3.5">
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-[#E1383B] to-[#EF7F1A] text-white shadow-sm shadow-[#E1383B]/25">
      {icon}
    </span>
    <div className="min-w-0">
      <h1 className="text-base font-bold leading-tight text-slate-900">{title}</h1>
      {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
    </div>
    {right && <div className="ml-auto">{right}</div>}
  </div>
);

export const EmptyState = ({
  icon,
  title,
  description,
  action,
}: {
  icon: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
}) => (
  <div className="flex flex-col items-center text-center">
    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#E1383B]/10 text-[#E1383B]">
      {icon}
    </span>
    <p className="mt-4 text-sm font-semibold text-slate-800">{title}</p>
    {description && <p className="mt-1 text-sm text-slate-500">{description}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

type TablePagerProps = {
  pageIndex: number;
  pageSize: number;
  pageCount: number;
  rowCount: number;
  totalCount: number;
  setPageSize: (size: number) => void;
  gotoPage: (index: number) => void;
};

export const TablePager = ({
  pageIndex,
  pageSize,
  pageCount,
  rowCount,
  totalCount,
  setPageSize,
  gotoPage,
}: TablePagerProps) => {
  const lastPage = Math.max(pageCount, 1);
  const canPreviousPage = pageIndex > 0;
  const canNextPage = pageIndex + 1 < lastPage;
  const rangeStart = rowCount ? pageIndex * pageSize + 1 : 0;
  const rangeEnd = pageIndex * pageSize + rowCount;

  return (
    <div className="flex flex-col gap-3 border-t border-slate-100 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
        <span>Rows per page</span>
        <ShowRows pageSize={pageSize} setPageSize={setPageSize} />
        <span className="hidden sm:inline">
          Showing{" "}
          <strong className="font-semibold text-slate-700">
            {rangeStart}–{rangeEnd}
          </strong>{" "}
          of <strong className="font-semibold text-slate-700">{totalCount.toLocaleString("en-IN")}</strong>
        </span>
      </div>

      <div className="flex items-center justify-end gap-1.5">
        <span className="mr-2 text-xs text-slate-500">
          Page <strong className="font-semibold text-slate-700">{pageIndex + 1}</strong> of{" "}
          <strong className="font-semibold text-slate-700">{lastPage}</strong>
        </span>
        <button
          type="button"
          onClick={() => gotoPage(0)}
          disabled={!canPreviousPage}
          title="First page"
          aria-label="First page"
          className={pagerBtn}
        >
          <ChevronsLeft size={14} />
        </button>
        <button
          type="button"
          onClick={() => gotoPage(pageIndex - 1)}
          disabled={!canPreviousPage}
          title="Previous page"
          aria-label="Previous page"
          className={pagerBtn}
        >
          <ChevronLeft size={14} />
        </button>
        <button
          type="button"
          onClick={() => gotoPage(pageIndex + 1)}
          disabled={!canNextPage}
          title="Next page"
          aria-label="Next page"
          className={pagerBtn}
        >
          <ChevronRight size={14} />
        </button>
        <button
          type="button"
          onClick={() => gotoPage(lastPage - 1)}
          disabled={!canNextPage}
          title="Last page"
          aria-label="Last page"
          className={pagerBtn}
        >
          <ChevronsRight size={14} />
        </button>
      </div>
    </div>
  );
};

export const thClass =
  "whitespace-nowrap border-b border-slate-200 bg-slate-50/95 px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-400 backdrop-blur first:pl-5 last:pr-5";

export const tdClass =
  "whitespace-nowrap border-b border-slate-100 px-4 py-3 text-[13px] text-slate-600 first:pl-5 last:pr-5";

// Billing APIs return only user ids, so names are looked up once per id (quietly, no loader/toast)
export const useUserNames = (ids: Array<number | null | undefined>) => {
  const [names, setNames] = useState<Record<number, string>>({});
  const requested = useRef(new Set<number>());
  const idsKey = Array.from(new Set(ids.filter((id): id is number => Boolean(id)))).sort().join(",");

  useEffect(() => {
    idsKey
      .split(",")
      .filter(Boolean)
      .map(Number)
      .filter((id) => !requested.current.has(id))
      .forEach((id) => {
        requested.current.add(id);
        getUserDataByIdApi(id)
          .then(({ data }) => {
            const name = data?.data?.name;
            if (name) setNames((prev) => ({ ...prev, [id]: name }));
          })
          .catch(() => undefined);
      });
  }, [idsKey]);

  return useCallback(
    (name?: string | null, id?: number | null) => name || (id ? names[id] ?? `User #${id}` : undefined),
    [names]
  );
};

type DeleteConfirmModalProps = {
  isOpen: boolean;
  title: string;
  description?: ReactNode;
  details?: Array<{ label: string; value?: ReactNode }>;
  confirmLabel?: string;
  isProcessing?: boolean;
  onConfirm: () => void;
  onClose: () => void;
};

// Destructive-action dialog: warning icon, what is being deleted, and an explicit red confirm
export const DeleteConfirmModal = ({
  isOpen,
  title,
  description,
  details,
  confirmLabel = "Delete",
  isProcessing,
  onConfirm,
  onClose,
}: DeleteConfirmModalProps) => {
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  // Latest onClose without re-running the effect (and stealing focus) on every parent render
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) return;
    cancelRef.current?.focus();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !isProcessing) onCloseRef.current();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen, isProcessing]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[1300] flex items-center justify-center bg-slate-900/40 px-4 backdrop-blur-[2px]"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !isProcessing) onClose();
      }}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="delete-confirm-title"
        className="w-full max-w-md animate-fade-in overflow-hidden rounded-2xl bg-white shadow-xl ring-1 ring-slate-200"
      >
        <div className="flex gap-4 px-6 pb-4 pt-6">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-red-50 text-red-600 ring-8 ring-red-50/50">
            <AlertTriangle size={20} />
          </span>
          <div className="min-w-0 flex-1">
            <h2 id="delete-confirm-title" className="text-base font-semibold text-slate-900">
              {title}
            </h2>
            {description && <div className="mt-1 text-sm text-slate-500">{description}</div>}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isProcessing}
            className="-mr-2 -mt-2 flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {details && details.length > 0 && (
          <dl className="mx-6 mb-4 divide-y divide-slate-100 rounded-xl border border-slate-200 bg-slate-50/60">
            {details.map((item) => (
              <div key={item.label} className="flex items-center justify-between gap-4 px-4 py-2 text-sm">
                <dt className="text-slate-500">{item.label}</dt>
                <dd className="truncate text-right font-medium text-slate-800">
                  {item.value === undefined || item.value === null || item.value === "" ? "—" : item.value}
                </dd>
              </div>
            ))}
          </dl>
        )}

        <p className="mx-6 mb-5 flex items-center gap-1.5 text-xs text-red-600">
          <AlertTriangle size={12} />
          This action can't be undone.
        </p>

        <div className="flex justify-end gap-2 border-t border-slate-100 bg-slate-50/60 px-6 py-3">
          <button ref={cancelRef} type="button" className={ghostBtn} onClick={onClose} disabled={isProcessing}>
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isProcessing}
            className="inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-red-600 px-3.5 text-sm font-semibold text-white shadow-sm transition hover:bg-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Trash2 size={15} />
            {isProcessing ? "Deleting..." : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};
